import { computed, inject, Injectable } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { _stringMapValues, IsoDate, localDate } from '@naturalcycles/js-lib'
import {
  Assignments,
  Experiment,
  Goal,
  OvulationStatus,
  ProductKey,
  UserFertility,
} from '@naturalcycles/shared'
import { EVENT } from '@src/app/analytics/analytics.cnst.js'
import { AnalyticsService } from '@src/app/analytics/analytics.service.js'
import { QuizService } from '@src/app/srv/quiz/quiz.service.js'
import { getState, select } from '@src/app/srv/store.service.js'

@Injectable({ providedIn: 'root' })
export class OuraSyncABTestService {
  private quizService = inject(QuizService)
  private analyticsService = inject(AnalyticsService)

  private experiment = toSignal(select(s => s.experiment))
  private quiz = toSignal(select(s => s.quiz))
  private userFertility = toSignal(select(s => s.userFertility))

  public ovulation = computed<OuraSyncABTestOvulation>(() => {
    const uf = this.userFertility()
    if (!uf) return { status: OuraSyncABTestOvulationStatus.UNKNOWN }
    return this.getOvulation(uf)
  })

  public shouldSyncOuraBeforePayment = computed<boolean>(() => {
    const experiment = this.experiment()
    const quiz = this.quiz()

    if (!quiz || !experiment) return false

    const { assignments } = experiment
    const { data, flow } = quiz

    if (!this.isAnyTestBucket(assignments) || !data) {
      return false
    }

    // We only want to sync before payment if user already owns Oura Ring
    const deviceRecommendation = this.quizService.getRecommendedDevice(data, flow)
    if (deviceRecommendation !== ProductKey.OURA_RING_DISCOUNT) {
      return false
    }

    const ownsOuraRing = this.quizService.ownsRecommendedDevice(quiz)
    if (!ownsOuraRing) {
      return false
    }

    // We only want to sync before payment if at least 2 last period dates are provided
    const { lastPeriodDates } = data
    if (!lastPeriodDates || lastPeriodDates.length < 2) {
      return false
    }

    return true
  })

  public isTestBucket(assignments?: Assignments): boolean {
    assignments ||= getState().experiment.assignments
    return assignments[Experiment.OURA_SYNC_BEFORE_PAYMENT_V3]?.bucket === 'test'
  }

  public isTest2Bucket(assignments?: Assignments): boolean {
    assignments ||= getState().experiment.assignments
    return assignments[Experiment.OURA_SYNC_BEFORE_PAYMENT_V3]?.bucket === 'test2'
  }

  public isAnyTestBucket(assignments?: Assignments): boolean {
    assignments ||= getState().experiment.assignments
    return ['test', 'test2'].includes(
      assignments[Experiment.OURA_SYNC_BEFORE_PAYMENT_V3]?.bucket || '',
    )
  }

  public isAnyTestAssignment(assignments?: Assignments): boolean {
    assignments ||= getState().experiment.assignments
    return !!assignments[Experiment.OURA_SYNC_BEFORE_PAYMENT_V3]?.bucket
  }

  private getOvulation(uf: UserFertility): OuraSyncABTestOvulation {
    if (!uf.colorMap || uf.ncycles <= 0) {
      return { status: OuraSyncABTestOvulationStatus.UNKNOWN }
    }

    const cycle = uf.ncycles - 1 // The cycle before the current cycle

    const colorCode = _stringMapValues(uf.colorMap).find(cc => {
      if (cc.nCycle !== cycle) return false
      if (!cc.ovulationStatus) return false
      if ([OvulationStatus.OVU_PREDICTION, OvulationStatus.OVU_DAY].includes(cc.ovulationStatus)) {
        return false
      }
      return true
    })

    if (!colorCode) {
      return { status: OuraSyncABTestOvulationStatus.UNKNOWN }
    }

    if (colorCode.ovulationStatus === OvulationStatus.ANOVULATORY) {
      return { status: OuraSyncABTestOvulationStatus.DID_NOT_OVULATE, date: colorCode.date }
    }

    if (
      [OvulationStatus.OVU_NOT_CONFIRMED, OvulationStatus.OVU_WAITING].includes(
        colorCode.ovulationStatus!,
      )
    ) {
      return { status: OuraSyncABTestOvulationStatus.CAN_NOT_CONFIRM, date: colorCode.date }
    }

    if (colorCode.ovulationStatus === OvulationStatus.OVU_CONFIRMED) {
      if (uf.ovulationWindowMax[cycle] === uf.ovulationWindowMin[cycle]) {
        return { status: OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_DATE, date: colorCode.date }
      }

      const date = localDate(colorCode.date)
      const minDiff = uf.ovulationWindowMin[cycle]! - colorCode.cd
      const maxDiff = uf.ovulationWindowMax[cycle]! - colorCode.cd

      const range: [IsoDate, IsoDate] = [
        date.plusDays(minDiff).toISODate(),
        date.plusDays(maxDiff).toISODate(),
      ]
      return { status: OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_RANGE, range }
    }

    return { status: OuraSyncABTestOvulationStatus.UNKNOWN }
  }

  public getOuraInsightsButtonLabel(
    slug: OuraSyncABtestInsightSlug,
    predictionResult?: OuraSyncABTestOvulationStatus,
  ): string {
    if (this.isTest2Bucket()) {
      return 'Unlock today’s insights'
    }

    switch (slug) {
      case OuraSyncABtestInsightSlug.animation:
        return 'Find my ovulation'
      case OuraSyncABtestInsightSlug.graph:
        return 'Continue'
      case OuraSyncABtestInsightSlug.image:
        return predictionResult === OuraSyncABTestOvulationStatus.CAN_NOT_CONFIRM
          ? 'Start my journey'
          : 'Unlock today’s insights'
    }
  }

  public reportWhetherShouldSyncOuraBeforePayment(isEligible: boolean): void {
    this.analyticsService.trackEvent(EVENT.CHECKED_OURA_SYNC_BEFORE_PAYMENT_ELIGIBLILITY, {
      isEligible,
    })
  }
}

type ImageByResultAndGoalMap = Record<OuraSyncABTestOvulationStatus, Partial<Record<Goal, string>>>

interface OvulationUnknown {
  status: OuraSyncABTestOvulationStatus.UNKNOWN
}

interface OvulationWithDate {
  status:
    | OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_DATE
    | OuraSyncABTestOvulationStatus.DID_NOT_OVULATE
    | OuraSyncABTestOvulationStatus.CAN_NOT_CONFIRM
  date: IsoDate
}

interface OvulationWithDateRange {
  status: OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_RANGE
  range: [IsoDate, IsoDate]
}

export enum OuraSyncABtestInsightSlug {
  animation = 'animation',
  graph = 'graph',
  image = 'image',
}

export enum OuraSyncABTestOvulationStatus {
  OVUL_SPECIFIC_RANGE = 1,
  OVUL_SPECIFIC_DATE = 2,
  DID_NOT_OVULATE = 3,
  CAN_NOT_CONFIRM = 4,
  UNKNOWN = 5,
}

export const ourSyncABTestOvulationStatusMap: Record<OuraSyncABTestOvulationStatus, string> = {
  [OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_RANGE]: 'Ovulated withing specific range',
  [OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_DATE]: 'Ovulated on specific date',
  [OuraSyncABTestOvulationStatus.DID_NOT_OVULATE]: "Didn't ovulate",
  [OuraSyncABTestOvulationStatus.CAN_NOT_CONFIRM]: "Can't confirm",
  [OuraSyncABTestOvulationStatus.UNKNOWN]: 'Unknown',
}

export const imageByResultAndGoalMap: ImageByResultAndGoalMap = {
  [OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_DATE]: {
    [Goal.PLAN]: 'assets/img/ab322/ovul_pred_1.jpg',
    [Goal.PREVENT]: 'assets/img/ab322/ovul_pred_2.jpg',
  },

  [OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_RANGE]: {
    [Goal.PLAN]: 'assets/img/ab322/ovul_pred_1.jpg',
    [Goal.PREVENT]: 'assets/img/ab322/ovul_pred_2.jpg',
  },
  [OuraSyncABTestOvulationStatus.DID_NOT_OVULATE]: {
    [Goal.PLAN]: 'assets/img/ab322/ovul_pred_2.jpg',
    [Goal.PREVENT]: 'assets/img/ab322/ovul_pred_2.jpg',
  },
  [OuraSyncABTestOvulationStatus.CAN_NOT_CONFIRM]: {
    [Goal.PLAN]: 'assets/img/ab322/ovul_pred_3.jpg',
    [Goal.PREVENT]: 'assets/img/ab322/ovul_pred_3.jpg',
  },
  [OuraSyncABTestOvulationStatus.UNKNOWN]: {
    [Goal.PLAN]: 'assets/img/ab322/ovul_pred_3.jpg',
    [Goal.PREVENT]: 'assets/img/ab322/ovul_pred_3.jpg',
  },
}

export type OuraSyncABTestOvulation = OvulationWithDate | OvulationWithDateRange | OvulationUnknown
